import { GetStaticPaths, GetStaticProps } from "next";
import NextImage from "next/image";
import Link from "next/link";
import {
  Breadcrumbs as Modes0Breadcrumbs,
  PageWithSidebarLayout,
  SidebarArticle,
  type Crumb,
  type SidebarArticleItem,
} from "@modes0/ui-components";
import {
  getServiceSubCategories,
  getServiceSubCategoryBySlug,
} from "@/content/services";
import {
  BUSINESS_NAME,
  WEBSITE_URL,
  createServiceContactFormContent,
} from "@/content";
import {
  getAllUnRelatedSubCategoriesByCategory,
  getRelatedSubCategoriesByCategory,
} from "@/lib";
import ContentArticleReader from "@/ui/content-article-reader/content-article-reader";
import ServiceContactForm from "@/widgets/contact-form/ServiceContactForm";
import type { ContentBlock, Image as ContentImage, SubCategory } from "@/types";
import {PageSeo, buildBreadcrumbSchema, buildServiceSchema} from '@modes0/ui-components-seo';
import {getBreadcrumbSeoItems, getServiceSeoInput, seoSiteProfile} from '@/content/seo';

type ServicePageProps = {
  categorySlug: string;
  serviceSlug: string;
};

const brandColor = "#28aae1";
const pageBackgroundColor = "#f5f8fb";
const visibleSidebarItems = 4;

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

const getCategoryHref = (service: SubCategory) =>
  `/${service.headCategory.categoryType}/${service.headCategory.slug}`;

const getServiceHref = (service: SubCategory) =>
  service.link || `${getCategoryHref(service)}/${service.slug}`;

const getImageSrc = (image?: Partial<ContentImage>) =>
  image?.url || image?.original || image?.thumbnail || "";

const toContentImage = (
  service: SubCategory,
  image: Partial<ContentImage> | undefined,
  index = 0,
): ContentImage | null => {
  const src = getImageSrc(image) || service.heroImage?.url || "";

  if (!src) {
    return null;
  }

  const title =
    image?.title ||
    image?.originalTitle ||
    image?.thumbnailTitle ||
    service.heroImage?.title ||
    service.title;
  const alt =
    image?.alt ||
    image?.originalAlt ||
    image?.thumbnailAlt ||
    service.heroImage?.alt ||
    title;

  return {
    ...image,
    id: image?.id || service.heroImage?.id || service.id || index + 1,
    url: image?.url || src,
    original: image?.original || src,
    thumbnail: image?.thumbnail || src,
    title,
    alt,
    originalAlt: image?.originalAlt || alt,
    thumbnailAlt: image?.thumbnailAlt || alt,
    thumbnailTitle: image?.thumbnailTitle || title,
  } as ContentImage;
};

const getServiceImages = (service: SubCategory) =>
  (service.images || [])
    .map((image, index) =>
      toContentImage(service, image as Partial<ContentImage>, index),
    )
    .filter((image): image is ContentImage => Boolean(image));

const getServiceImage = (service: SubCategory) =>
  getServiceImages(service)[0] || toContentImage(service, undefined);

const getSidebarArticleImage = (
  service: SubCategory,
): SidebarArticleItem["image"] | undefined => {
  const image = getServiceImage(service);

  if (!image) {
    return undefined;
  }

  return {
    src: image.url,
    url: image.url,
    original: image.original,
    thumbnail: image.thumbnail,
    alt: image.alt || service.title,
    title: image.title || service.title,
  };
};

const getSidebarArticleItems = (
  services: SubCategory[],
): SidebarArticleItem[] =>
  services.slice(0, visibleSidebarItems).map((service) => ({
    id: `${service.headCategory.id}-${service.id}`,
    title: service.title,
    description: service.description,
    href: getServiceHref(service),
    ctaLabel: "View service",
    image: getSidebarArticleImage(service),
    accentColor: brandColor,
  }));

const getOrderedContentBlocks = (
  content: SubCategory["content"],
): ContentBlock[] =>
  [...(content || [])].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

const joinServiceNames = (services: SubCategory[]) => {
  const names = services.map((service) => service.title.toLowerCase());

  if (names.length <= 1) {
    return names[0] || "";
  }

  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

const getRelatedParagraph = (
  service: SubCategory,
  relatedServices: SubCategory[],
) => {
  const relatedNames = joinServiceNames(relatedServices.slice(0, 2));

  if (!relatedNames) {
    return service.description;
  }

  return `${service.description} If the job points to a wider ${service.headCategory.name.toLowerCase()} need, ${BUSINESS_NAME} can also advise on ${relatedNames} so the security plan supports the whole property.`;
};

const getBreadcrumbData = (service: SubCategory) => {
  const categoryTitle = capitalize(service.headCategory.categoryType);
  const categoryHref = `/${service.headCategory.categoryType}`;
  const headCategoryHref = getCategoryHref(service);
  const currentHref = getServiceHref(service);

  const crumbs: Crumb[] = [
    { title: "Home", href: "/" },
    { title: categoryTitle, href: categoryHref },
    { title: service.headCategory.name, href: headCategoryHref },
    { title: service.title },
  ];

  const itemListElements = [
    {
      position: 1,
      name: "Home",
      item: WEBSITE_URL,
    },
    {
      position: 2,
      name: categoryTitle,
      item: `${WEBSITE_URL}${categoryHref}`,
    },
    {
      position: 3,
      name: service.headCategory.name,
      item: `${WEBSITE_URL}${headCategoryHref}`,
    },
    {
      position: 4,
      name: service.title,
      item: `${WEBSITE_URL}${currentHref}`,
    },
  ];

  return { crumbs, itemListElements };
};

const ServicesSidebar = ({
  relatedServices,
  otherServices,
}: {
  relatedServices: SubCategory[];
  otherServices: SubCategory[];
}) => (
  <div className="space-y-6">
    <SidebarArticle
      items={getSidebarArticleItems(relatedServices)}
      Link={Link}
      title="Related services"
      titleComponent="h2"
      description="More security services in this category."
      itemCtaLabel="View service"
      accentColor={brandColor}
      maxWidth="100%"
      imageWidth={82}
      imageHeight={68}
      ariaLabel="Related services"
    />
    <SidebarArticle
      items={getSidebarArticleItems(otherServices)}
      Link={Link}
      title="Other services"
      titleComponent="h2"
      description={`Browse other security services available from ${BUSINESS_NAME}.`}
      itemCtaLabel="View service"
      accentColor={brandColor}
      maxWidth="100%"
      imageWidth={82}
      imageHeight={68}
      ariaLabel="Other services"
    />
  </div>
);

const ServiceMainContent = ({
  service,
  relatedServices,
}: {
  service: SubCategory;
  relatedServices: SubCategory[];
}) => {
  const serviceImage = getServiceImage(service);
  const orderedContentBlocks = getOrderedContentBlocks(service.content);
  const [introContentBlock, ...remainingContentBlocks] = orderedContentBlocks;
  const relatedParagraph = getRelatedParagraph(service, relatedServices);
  const { crumbs } = getBreadcrumbData(service);

  return (
    <div className="min-w-0 bg-white px-4 py-5 md:px-6 md:py-6">
      <div className="mb-6">
        <Modes0Breadcrumbs crumbs={crumbs} currentPageAccent={brandColor} />
      </div>
      <article className="min-w-0">
        <header className="mx-auto max-w-[680px] text-center">
          <h1 className="m-0 text-balance text-3xl font-black leading-tight tracking-normal text-[#050505] md:text-[42px] md:leading-[1.06]">
            {service.title}
          </h1>
          {service.description ? (
            <p className="mx-auto mt-4 max-w-[620px] text-pretty text-base font-semibold leading-7 text-[#4a4a4a] md:text-lg md:leading-8">
              {service.description}
            </p>
          ) : null}
        </header>
        <div className="mt-6 min-w-0">
          {serviceImage && (
            <figure className="mb-8 overflow-hidden rounded-lg border border-black/10 bg-white shadow-sm">
              <div className="relative h-[260px] w-full md:h-[420px]">
                <NextImage
                  src={serviceImage.url}
                  alt={serviceImage.alt || service.title}
                  fill
                  sizes="(min-width: 1024px) 820px, calc(100vw - 32px)"
                  className="object-cover"
                  loading="lazy"
                />
              </div>
              <figcaption className="border-t border-black/10 px-4 py-3 text-sm leading-6 text-neutral-700">
                {serviceImage.caption ||
                  serviceImage.description ||
                  serviceImage.title ||
                  `${service.title} by ${BUSINESS_NAME}.`}
              </figcaption>
            </figure>
          )}

          {introContentBlock && (
            <ContentArticleReader contentBlocks={[introContentBlock]} />
          )}

          {relatedParagraph && (
            <p
              className="my-6 border-l-4 bg-[#f8f8f8] px-4 py-4 text-base leading-7 text-neutral-800 md:text-lg md:leading-8"
              style={{ borderLeftColor: brandColor }}
            >
              {relatedParagraph}
            </p>
          )}

          {remainingContentBlocks.length > 0 && (
            <ContentArticleReader contentBlocks={remainingContentBlocks} />
          )}
        </div>
      </article>
    </div>
  );
};

const ServiceDetailLayout = ({ service }: { service: SubCategory }) => {
  const relatedServices = getRelatedSubCategoriesByCategory(service) ?? [];
  const otherServices = getAllUnRelatedSubCategoriesByCategory(service) ?? [];

  return (
    <section
      className="w-full py-10 md:py-14"
      style={{ backgroundColor: pageBackgroundColor }}
    >
      <PageWithSidebarLayout
        maxWidth="xl"
        gridTemplateColumns={{
          lg: "2fr 1fr",
          xl: "2fr 1fr",
        }}
        main={
          <ServiceMainContent
            service={service}
            relatedServices={relatedServices}
          />
        }
        sidebar={
          <ServicesSidebar
            relatedServices={relatedServices}
            otherServices={otherServices}
          />
        }
      />
    </section>
  );
};

export const getStaticPaths: GetStaticPaths = () => ({
  paths: getServiceSubCategories().map((service) => ({
    params: {
      categorySlug: service.headCategory.slug,
      serviceSlug: service.slug,
    },
  })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<ServicePageProps> = ({
  params,
}) => ({
  props: {
    categorySlug: String(params?.categorySlug || ""),
    serviceSlug: String(params?.serviceSlug || ""),
  },
});

export default function ServicePage({
  categorySlug,
  serviceSlug,
}: ServicePageProps) {
  const service = getServiceSubCategoryBySlug(categorySlug, serviceSlug);

  if (!service) {
    return null;
  }

  const { itemListElements } = getBreadcrumbData(service);
  const serviceContactFormContent = createServiceContactFormContent({
    categoryName: service.headCategory.name,
    categorySlug: service.headCategory.slug,
    pageUrl: `${WEBSITE_URL}${getServiceHref(service)}`,
    serviceSlug: service.slug,
    serviceTitle: service.title,
  });

  return (
    <>
      <PageSeo
        meta={service.metaTags}
        siteProfile={seoSiteProfile}
        schemas={[
          {
            id: `${service.slug}-service-json-ld`,
            schema: buildServiceSchema(getServiceSeoInput(service), seoSiteProfile),
          },
          {
            id: `${service.slug}-breadcrumbs-json-ld`,
            schema: buildBreadcrumbSchema(getBreadcrumbSeoItems(itemListElements), seoSiteProfile),
          },
        ]}
      />
      <ServiceDetailLayout service={service} />
      <section className="bg-white px-4 py-10 md:py-14">
        <div className="mx-auto max-w-3xl">
          <ServiceContactForm content={serviceContactFormContent} />
        </div>
      </section>
    </>
  );
}
